// Frontend/src/lib/paymentStyles.ts
//
// One visual vocabulary for invoices, the way issueStyles.ts is for issues.
//
// The problem it solves: there are EIGHT invoice statuses, and the old badge
// gave four of them the same blue (DRAFT, SENT, UNPAID, PENDING_HOA_TRANSFER)
// and two the same amber (PARTIALLY_PAID, PENDING_VERIFICATION). In a list of
// forty invoices the colour carried almost no information — you had to read
// every badge.
//
// Two axes, and only one of them is loud:
//
//   WAITING ON  owns colour   — who has the ball: them, you, the platform,
//                               or nobody. This is what an admin actually
//                               scans for, and it collapses eight statuses
//                               into four things worth telling apart.
//   STATUS      owns the word — the precise state, as the chip's label.
//   OVERDUE     is a separate red modifier, like URGENT was for issues.
//
// Colours come from the app's own tokens in index.css: primary #1F3A93,
// accent #27AE60, destructive #DC2626.
//
// Inline CSSProperties rather than Tailwind classes, because this is a .ts
// file and a class string appearing only outside a .tsx file is one the
// scanner can miss — it looks right in dev and ships unstyled.

import type { CSSProperties } from "react";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  FileEdit,
  Hourglass,
  PiggyBank,
  Send,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

import type { InvoiceStatus } from "@/api/payments";

export const BRAND = "#1F3A93";
export const BRAND_ACCENT = "#27AE60";

export type Tone = { rail: string; bg: string; fg: string; ring: string };

/* ------------------------------------------------------------------ *
 * Who has the ball
 * ------------------------------------------------------------------ */
export type WaitingOn = "NOBODY" | "RESIDENT" | "YOU" | "PLATFORM" | "SETTLED";

export const WAITING_TONE: Record<WaitingOn, Tone> = {
  // Not sent yet. The quietest thing on the page — it is not waiting on
  // anyone until someone sends it.
  NOBODY: { rail: "#94A3B8", bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
  // The resident owes money. Brand blue: the normal, healthy state.
  RESIDENT: { rail: BRAND, bg: "#E7EBF8", fg: "#1B3480", ring: "#C3CDEC" },
  // YOU have to do something. The only warm colour in the set, on purpose.
  YOU: { rail: "#EA580C", bg: "#FFEDD5", fg: "#9A3412", ring: "#FCC89B" },
  // Money is in transit through the platform; nobody local can act.
  PLATFORM: { rail: "#7C3AED", bg: "#EDE9FE", fg: "#5B21B6", ring: "#C9BCF5" },
  // Done, or closed. Recedes.
  SETTLED: { rail: BRAND_ACCENT, bg: "#DCF5E6", fg: "#12693C", ring: "#B4E4C7" },
};

/**
 * Money the platform says it has sent the HOA, which the HOA has not yet
 * confirmed arriving in its bank account.
 *
 * Four conditions, not a status — the status is PAID, identical to a fully
 * settled invoice. That is exactly why these used to render green and read
 * as finished business while actually waiting on the admin to act, and why
 * this cannot be expressed as a value in the status dropdown.
 *
 * The fields all come down on the ordinary invoice list, so this is a
 * predicate rather than a second endpoint.
 */
export function isAwaitingHoaConfirmation(inv: {
  status: InvoiceStatus;
  payment_flow?: string | null;
  forwarded_at?: string | null;
  finalized_at?: string | null;
}): boolean {
  return (
    inv.status === "PAID" &&
    inv.payment_flow === "PLATFORM_RELAY" &&
    !!inv.forwarded_at &&
    !inv.finalized_at
  );
}

export function waitingOn(
  status: InvoiceStatus,
  awaitingConfirmation = false
): WaitingOn {
  // Checked before the switch: the status here is PAID, so the switch would
  // send it to SETTLED and the rail would go green on an invoice that still
  // needs the admin to tick it off.
  if (awaitingConfirmation) return "YOU";

  switch (status) {
    case "DRAFT":
      return "NOBODY";
    case "SENT":
    case "UNPAID":
    case "PARTIALLY_PAID":
      return "RESIDENT";
    case "PENDING_VERIFICATION":
      return "YOU";
    case "PENDING_HOA_TRANSFER":
      return "PLATFORM";
    case "PAID":
    case "CANCELLED":
      return "SETTLED";
    default:
      return "NOBODY";
  }
}

/* ------------------------------------------------------------------ *
 * The precise status — its own tone for the chip, its own icon
 *
 * A cancelled invoice must not read as green just because it is settled,
 * so the chip keeps a per-status tone even though the rail follows the
 * coarser "waiting on" grouping.
 * ------------------------------------------------------------------ */
export const STATUS_TONE: Record<InvoiceStatus, Tone> = {
  DRAFT: { rail: "#94A3B8", bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" },
  SENT: { rail: "#0284C7", bg: "#E0F2FE", fg: "#075985", ring: "#B6E2F8" },
  UNPAID: { rail: BRAND, bg: "#E7EBF8", fg: "#1B3480", ring: "#C3CDEC" },
  PARTIALLY_PAID: { rail: "#D97706", bg: "#FEF3C7", fg: "#92400E", ring: "#FDE0A3" },
  PENDING_VERIFICATION: { rail: "#EA580C", bg: "#FFEDD5", fg: "#9A3412", ring: "#FCC89B" },
  PENDING_HOA_TRANSFER: { rail: "#7C3AED", bg: "#EDE9FE", fg: "#5B21B6", ring: "#C9BCF5" },
  PAID: { rail: BRAND_ACCENT, bg: "#DCF5E6", fg: "#12693C", ring: "#B4E4C7" },
  CANCELLED: { rail: "#94A3B8", bg: "#F1F5F9", fg: "#64748B", ring: "#E2E8F0" },
};

export const STATUS_ICON: Record<InvoiceStatus, LucideIcon> = {
  DRAFT: FileEdit,
  SENT: Send,
  UNPAID: Clock,
  PARTIALLY_PAID: PiggyBank,
  PENDING_VERIFICATION: Hourglass,
  PENDING_HOA_TRANSFER: ShieldCheck,
  PAID: CheckCircle2,
  CANCELLED: Ban,
};

export const OVERDUE_TONE: Tone = {
  rail: "#DC2626",
  bg: "#FEE2E2",
  fg: "#991B1B",
  ring: "#F8B4B4",
};

export const OVERDUE_ICON = AlertTriangle;

/** Statuses where money is still owed, so "overdue" means something. */
const COLLECTABLE: InvoiceStatus[] = ["SENT", "UNPAID", "PARTIALLY_PAID"];

/**
 * Overdue is a fact about a due date, not a status.
 *
 * The API sends `is_overdue`, but it is optional and older rows may not
 * carry it, so the date is the fallback. A PAID invoice past its due date
 * is not overdue — it is paid.
 */
export function isOverdue(
  status: InvoiceStatus,
  dueDate?: string | null,
  apiFlag?: boolean
): boolean {
  if (!COLLECTABLE.includes(status)) return false;
  if (apiFlag !== undefined) return apiFlag;
  if (!dueDate) return false;
  return new Date(dueDate) < new Date(new Date().toDateString());
}

/** A settled invoice is finished business; let it recede in a list. */
export function isSettled(status: InvoiceStatus): boolean {
  return status === "PAID" || status === "CANCELLED";
}

/* ------------------------------------------------------------------ *
 * Ready-made style objects
 * ------------------------------------------------------------------ */
export function chipStyle(tone: Tone): CSSProperties {
  return { background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1px ${tone.ring}` };
}

export function quietChipStyle(): CSSProperties {
  return { background: "#F8FAFC", color: "#475569", boxShadow: "inset 0 0 0 1px #E5E7EB" };
}

/**
 * The card's left rail, as a rounded left BORDER rather than a positioned
 * bar — the dashboard's construction, so it follows the corner radius
 * instead of running square into it.
 *
 * Overdue overrides the rail: an invoice nobody has paid and that is now
 * late is the one thing in the list that should catch the eye.
 */
export function cardRailStyle(
  status: InvoiceStatus,
  overdue: boolean,
  awaitingConfirmation = false
): CSSProperties {
  return {
    borderLeftColor: overdue
      ? OVERDUE_TONE.rail
      : WAITING_TONE[waitingOn(status, awaitingConfirmation)].rail,
  };
}

export function iconWellStyle(status: InvoiceStatus, overdue: boolean): CSSProperties {
  const tone = overdue ? OVERDUE_TONE : STATUS_TONE[status];
  return { background: tone.bg, color: tone.fg };
}

/** The dashboard's Stat-tile treatment: soft ground, matching ring. */
export function tileStyle(tone: Tone): CSSProperties {
  return { background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1px ${tone.ring}` };
}
